import {Stage as KonvaStage, Layer, Rect, Transformer} from 'react-konva';
import React, {
  useEffect,
  useState,
  useRef,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import {
  Box,
  Point,
  BoxLabelProps,
  LabelProps,
  LineLabelProps,
  PolyLabelProps,
  TooltipEvent,
} from './types';
import {genId, SCALE_FACTOR, SELECTION_BOX, TRANSFORMER} from './const';
import {Label, LABEL_NAME} from './Label';
import {KonvaPointerEvent} from 'konva/types/PointerEvents';
import {without} from 'lodash';
import {CircularProgress, Tooltip} from '@material-ui/core';
import {Image, IMAGE_NAME} from './Image';
import Konva from 'konva';
import {KonvaEventObject} from 'konva/types/Node';
import {AnnotatorContext, NOT_SCALE_TO_FIT} from './Provider';
import Hotkeys from 'react-hot-keys';
import BrokenImage from '@material-ui/icons/BrokenImage';
import grey from '@material-ui/core/colors/grey';
import {
  MAX_SCALE_FACTOR,
  MIN_SCALE_FACTOR,
} from '../../components/DataPrepare/DetectionStep/const';
import './Annotator.scss';
import {Polygon} from './Polygon';
import {Line} from './Line';
import {ImageType} from '../../types/dataset/DatasetGetAnnotationsResponse';

const STAGE_NAME = 'stage';
const LOADER_SIZE = 100;
const MIN_LABEL_DIMENSION = 5;
export const STAGE_HEIGHT_ADJ = 50;
const CURSOR_MAP: {
  [key: string]: string;
} = {
  Stage: 'default',
  Default: 'default',
  Image: 'crosshair',
  Grabbing: 'grabbing',
};

type ImageProp = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ToolType = 'RECT' | 'LINE' | 'POLY' | 'SELECT';
export const ToolTypes: ToolType[] = ['RECT', 'LINE', 'POLY', 'SELECT'];

export interface AnnotatorProps {
  src?: string;
  lastLabelUsed?: string | null;
  image?: ImageType | null | undefined;
  width?: number;
  height?: number;
  boxLabels?: BoxLabelProps[];
  polyLabels?: PolyLabelProps[];
  lineLabels?: LineLabelProps[];
  selectedLabels: string[];
  zoomEnable?: boolean;
  fullToolset?: boolean;
  tool?: ToolType;
  onBoxLabelCreated?: (label: BoxLabelProps) => void;
  onPolyLabelCreated?: (label: PolyLabelProps) => void;
  onLineLabelCreated?: (label: LineLabelProps) => void;
  onSelect?: (ids: string[]) => void | void;
  onContextMenu?: (e: PointerEvent) => void | void;
  onDelete?: (id: string, tool?: ToolType) => void | void;
  onChange?: (target: BoxLabelProps, newProp: Partial<BoxLabelProps>) => void;
}

export function Annotator({
  image,
  boxLabels,
  width = 0,
  height = 0,
  src: rawSrc,
  lastLabelUsed,
  selectedLabels,
  polyLabels = [],
  lineLabels = [],
  tool = 'SELECT',
  fullToolset = false,

  onSelect,
  onDelete,
  onContextMenu,
  onChange = () => {},
  onBoxLabelCreated = () => {},
  onPolyLabelCreated = () => {},
  onLineLabelCreated = () => {},
}: AnnotatorProps) {
  const {imageLayer, scale, setImageLayer, setScaleToFitFactor} = useContext(
    AnnotatorContext
  );

  const [tooltip, setTooltip] = useState<{title: string; x: number; y: number}>({
    title: '',
    x: 0,
    y: 0,
  });

  const [selector, setSelector] = useState<LabelProps>({
    visible: false,
  });

  const [cursor, setCursor] = useState('default');
  const [isGrabbing, setIsGrabbing] = useState(false);
  const [isImageBusy, setIsImageBusy] = useState(false);
  const [isImageError, setIsImageError] = useState(false);
  const [isTransforming, setIsTransforming] = useState(false);
  // Used for POLY and LINE tools
  const [localPolyLabel, setLocalPolyLabel] = useState<PolyLabelProps[]>([]);
  const [localLineLabels, setLocalLineLabels] = useState<LineLabelProps[]>([]);

  const layerRef = useRef<Konva.Layer>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const imageLayerRef = useRef<Konva.Layer>(null);
  const transformer = useRef<Konva.Transformer>(null);

  const [src, setSrc] = useState(rawSrc);
  const imageWidth = useMemo(() => image?.width || 0, [image]);
  const imageHeight = useMemo(() => image?.height || 0, [image]);

  const [mousePosition, setMousePosition] = useState<Point>({x: 0, y: 0});

  const getScaleDir = useCallback((containerSize: number, objectSize: number) => {
    return containerSize < objectSize ? (containerSize > objectSize ? 1 : -1) : -1;
  }, []);

  const x = useMemo(() => ((imageWidth - width) / 2) * getScaleDir(width, imageWidth), [
    width,
    imageWidth,
    getScaleDir,
  ]);

  const y = useMemo(
    () => ((imageHeight - height) / 2) * getScaleDir(height, imageHeight),
    [imageHeight, height, getScaleDir]
  );

  const scaledImage: ImageProp = useMemo(
    () => ({
      x,
      y,
      width: imageWidth * scale(),
      height: imageHeight * scale(),
    }),
    [x, y, imageWidth, imageHeight, scale]
  );

  const getScaleBoxLabel = useCallback(
    (boxLabel: BoxLabelProps) => {
      return {
        ...boxLabel,
        height: boxLabel.height * scale(),
        width: boxLabel.width * imageLayer.scale,
        x: imageLayer.x + x * imageLayer.scale + boxLabel.x * imageLayer.scale,
        y: imageLayer.y + y * imageLayer.scale + boxLabel.y * imageLayer.scale,
      };
    },
    [imageLayer.scale, imageLayer.x, imageLayer.y, x, y, scale]
  );

  const getScaleBoxLabels = useCallback(
    (refImage: ImageProp, boxLabels: BoxLabelProps[] = []) => {
      let scaledBoxLabels: BoxLabelProps[] = [];

      if (!!boxLabels?.length && refImage) {
        scaledBoxLabels = boxLabels.map(el => getScaleBoxLabel(el));
      }

      return scaledBoxLabels;
    },
    [getScaleBoxLabel]
  );

  const scaledBoxLabels = useMemo(() => getScaleBoxLabels(scaledImage, boxLabels), [
    scaledImage,
    boxLabels,
    getScaleBoxLabels,
  ]);

  useEffect(() => {
    if (polyLabels.length || fullToolset) {
      if (tool === 'POLY') {
        setLocalPolyLabel([
          ...polyLabels,
          {
            id: 'temp',
            points: [],
          },
        ]);
      } else {
        setLocalPolyLabel([...polyLabels]);
      }
    }
  }, [tool, polyLabels, fullToolset]);

  useEffect(() => {
    if (lineLabels.length || fullToolset) {
      if (tool === 'LINE') {
        setLocalLineLabels([
          ...lineLabels,
          {
            id: 'temp',
            points: [],
          },
        ]);
      } else {
        setLocalLineLabels([...lineLabels]);
      }
    }
  }, [tool, lineLabels, fullToolset]);

  const clean = useCallback(() => {
    setTooltip({...tooltip, title: ''}); // remove tooltip
    setIsImageError(false);
  }, [tooltip]);

  useEffect(() => {
    if (src !== rawSrc) {
      transformer?.current?.nodes([]);
      if (layerRef.current) {
        layerRef.current?.draw();
      }
      setSrc(rawSrc);
      clean();
    }
  }, [src, rawSrc, clean]);

  const disableSingleLabelTransform = useCallback(() => {
    const tr = transformer.current;

    tr?.nodes().map(node => node.setAttrs({draggable: false}));
    tr?.nodes([]);
  }, []);

  // get all groups with a function
  const findLabelObjById = useCallback((id: string | null | undefined) => {
    const stage = stageRef.current;

    return (
      stage?.find((node: Konva.Node) => {
        return node.getType() === 'Group' && node.id() === id;
      })[0] || null
    );
  }, []);

  // Initiates transform if selecton is one image, as well as newly created image
  useEffect(() => {
    if (selectedLabels.length > 1) {
      disableSingleLabelTransform();
    } else {
      const tr = transformer.current;

      if (selectedLabels.length === 1) {
        const node = findLabelObjById(selectedLabels[0]);

        node && tr && tr.nodes([node]);
        tr?.nodes()?.map(node => node.setAttrs({draggable: true}));
      } else {
        tr?.nodes([]);
      }
    }
  }, [selectedLabels, disableSingleLabelTransform, findLabelObjById]);

  const refreshCursor = useCallback(
    (target?: string) => {
      setCursor(CURSOR_MAP[isGrabbing ? 'Grabbing' : target || 'Stage']);
    },
    [isGrabbing]
  );

  useEffect(() => refreshCursor(), [refreshCursor]);

  const findBoxLabelPropById = useCallback(
    (id: string) => {
      return boxLabels ? boxLabels.find(boxLabel => boxLabel.id === id) : null;
    },
    [boxLabels]
  );

  const getScaledPointerPosition = useCallback(() => {
    const stage = stageRef.current;
    const pointerPos = stage?.getPointerPosition();
    const stageAttrs = stage?.attrs;
    const x = (pointerPos?.x || 0 - stageAttrs.x) / stageAttrs.scaleX;
    const y = (pointerPos?.y || 0 - stageAttrs.y) / stageAttrs.scaleY;

    return {x, y};
  }, []);

  const onStageMouseDown = useCallback(
    (event: KonvaPointerEvent) => {
      const pointerPos = getScaledPointerPosition();

      if (
        tool === 'RECT' &&
        !isGrabbing &&
        (event.target.hasName(IMAGE_NAME) || (!image && event.target.hasName(STAGE_NAME)))
      ) {
        setSelector({
          width: 0,
          height: 0,
          visible: true,
          x: pointerPos?.x,
          y: pointerPos?.y,
        });

        if (!event.evt.shiftKey && onSelect) {
          onSelect([]);
        }
      }
    },
    [isGrabbing, tool, image, onSelect, getScaledPointerPosition]
  );

  const onMousemove = useCallback(
    event => {
      const stage = stageRef.current;

      if (stage) {
        const pointerPosition = stage.getPointerPosition();

        if (pointerPosition && !isNaN(pointerPosition.x) && !isNaN(pointerPosition.y)) {
          setMousePosition(pointerPosition);

          if (tool === 'RECT' && selector.visible && selector) {
            const pointerPos = getScaledPointerPosition();

            if (pointerPos) {
              const leftLimit = imageLayer.x + x * imageLayer.scale;
              const rightLimit = leftLimit + imageWidth * imageLayer.scale;
              const topLimit = imageLayer.y + y * imageLayer.scale;
              const bottomLimit = topLimit + imageHeight * imageLayer.scale;
              const sX = selector.x || 0;
              const sY = selector.y || 0;

              setSelector({
                ...selector,
                width:
                  pointerPos.x < leftLimit
                    ? leftLimit - sX
                    : pointerPos.x > rightLimit
                    ? rightLimit - sX
                    : pointerPos.x - sX,
                height:
                  pointerPos.y < topLimit
                    ? topLimit - sY
                    : pointerPos.y > bottomLimit
                    ? bottomLimit - sY
                    : pointerPos.y - sY,
              });
            }
          }
        }
      }
    },
    [
      x,
      y,
      tool,
      selector,
      imageWidth,
      imageHeight,
      imageLayer.x,
      imageLayer.y,
      imageLayer.scale,
      getScaledPointerPosition,
    ]
  );

  useEffect(() => {
    const stageContainer = stageRef.current?.container();

    if (stageContainer) {
      stageContainer.addEventListener('mousemove', onMousemove, {passive: false});
    }

    return () => {
      if (stageContainer) {
        stageContainer.removeEventListener('mousemove', onMousemove);
      }
    };
  }, [onMousemove]);

  const getProperVal = useCallback((length: number = 0, pos: number = 0) => {
    return length < 0 ? pos + length : pos;
  }, []);

  const onMouseup = useCallback(
    (event: KonvaPointerEvent) => {
      const height = Math.abs(selector.height || 0);
      const width = Math.abs(selector.width || 0);

      if (
        selector.visible &&
        width >= MIN_LABEL_DIMENSION &&
        height >= MIN_LABEL_DIMENSION
      ) {
        const label: BoxLabelProps = {
          x:
            getProperVal(selector.width, selector.x) / imageLayer.scale -
            imageLayer.x / imageLayer.scale -
            x,
          y:
            getProperVal(selector.height, selector.y) / imageLayer.scale -
            imageLayer.y / imageLayer.scale -
            y,
          width: width / imageLayer.scale,
          height: height / imageLayer.scale,
          id: genId('RECT'),
        };

        onBoxLabelCreated(label);

        if (onSelect) {
          onSelect([...selectedLabels, label.id]);
        }

        if (
          event.target !== event.currentTarget &&
          !event.evt.shiftKey &&
          onContextMenu &&
          !lastLabelUsed
        ) {
          onContextMenu(event.evt);
        }
      } else if (event.target?.hasName(IMAGE_NAME) && onSelect) {
        onSelect([]);
      }

      setSelector({visible: false});
    },
    [
      selector,
      imageLayer,
      x,
      y,
      selectedLabels,
      lastLabelUsed,
      getProperVal,
      onBoxLabelCreated,
      onSelect,
      onContextMenu,
    ]
  );

  const onLabelClick = useCallback(
    (
      event: KonvaPointerEvent,
      label: BoxLabelProps | LineLabelProps | PolyLabelProps
    ) => {
      const target = event.currentTarget;

      target.moveToTop();
      transformer.current?.moveToTop();

      findLabelObjById(`${label.id}-tag`)?.moveToTop();

      if (target.hasName(LABEL_NAME)) {
        const metaPressed = event.evt.shiftKey || event.evt.ctrlKey || event.evt.metaKey;

        if (!metaPressed) {
          // Enable transform on single Label selection
          target.setAttrs({draggable: true});

          if (onSelect) {
            onSelect([label.id]);
          }
        } else if (onSelect) {
          const list = selectedLabels.includes(label.id)
            ? without(selectedLabels, label.id)
            : [...selectedLabels, label.id];

          onSelect(list);
        }
      } else if (tool === 'SELECT' && onSelect) {
        onSelect([label.id]);
      }
    },
    [selectedLabels, tool, onSelect, findLabelObjById]
  );

  const onStageContextMenu = useCallback((event: KonvaPointerEvent) => {
    event.evt.preventDefault();
  }, []);

  const onLabelContextMenu = useCallback(
    (
      event: KonvaPointerEvent,
      label: BoxLabelProps | LineLabelProps | PolyLabelProps
    ) => {
      onLabelClick(event, label);

      if (onContextMenu) {
        onContextMenu(event.evt);
      }
    },
    [onLabelClick, onContextMenu]
  );

  const onTooltip = useCallback(
    (event: Partial<TooltipEvent>) => {
      const ref = event.refObj;

      if (event.title) {
        setTooltip({
          y: ref?.y || 0,
          title: event.title,
          x: (ref?.width || 0) / 2 + (ref?.x || 0),
        });
      } else {
        clean();
      }
    },
    [clean]
  );

  const onTransformStart = useCallback(
    (event: KonvaPointerEvent) => {
      setIsTransforming(true);
      onTooltip({title: ''});
    },
    [onTooltip]
  );

  const onTransformEnd = useCallback(
    (event: KonvaPointerEvent) => {
      const labelObj: Konva.Shape | Konva.Stage = event.target;
      let label = findBoxLabelPropById(labelObj.id());

      if (label) {
        onChange(label, {
          ...label,
          width: labelObj.scaleX() * (label.width || 0),
          height: labelObj.scaleY() * (label.height || 0),
        });
      }

      labelObj.scaleX(1);
      labelObj.scaleY(1);

      if (transformer.current) {
        transformer.current.nodes([labelObj]);
      }

      setIsTransforming(false);
    },
    [findBoxLabelPropById, onChange]
  );

  const onDeleteAnnotation = useCallback(
    (id: string, tool: ToolType) => {
      transformer?.current?.nodes([]);

      if (onDelete) {
        onDelete(id, tool);
      }

      clean();
    },
    [onDelete, clean]
  );

  const onWheel = useCallback(
    (event: KonvaEventObject<WheelEvent>) => {
      event.evt.preventDefault();

      const newScale = Math.min(
        Math.max(
          event.evt.deltaY < 0 ? scale() * SCALE_FACTOR : scale() / SCALE_FACTOR,
          MIN_SCALE_FACTOR
        ),
        MAX_SCALE_FACTOR
      );
      const layerRef = imageLayerRef.current;
      const stage = event.target.getStage();
      const oldScale = layerRef?.scaleX() || 0;
      const pointerPosition = stage?.getPointerPosition() || {x: 0, y: 0};

      if (stage) {
        const mousePointTo = {
          x: (pointerPosition.x || 0) / oldScale - (layerRef?.x() || 0) / oldScale,
          y: pointerPosition.y / oldScale - (layerRef?.y() || 0) / oldScale,
        };

        setScaleToFitFactor(NOT_SCALE_TO_FIT);
        setImageLayer({
          scale: newScale,
          x: (pointerPosition.x / newScale - mousePointTo.x) * newScale,
          y: (pointerPosition.y / newScale - mousePointTo.y) * newScale,
        });
      }
    },
    [scale, setScaleToFitFactor, setImageLayer]
  );

  const onStageMouseOver = useCallback(
    (event: KonvaEventObject<MouseEvent>) => {
      const target: string = event.target.constructor.name;

      refreshCursor(target);
    },
    [refreshCursor]
  );

  const hotKey: {[key: string]: Function} = useMemo(
    () => ({
      spaceUp: () => setIsGrabbing(false),
      spaceDown: () => setIsGrabbing(true),
    }),
    []
  );

  const onLabelDrag = useCallback(
    (label: BoxLabelProps, props: Partial<BoxLabelProps>) => {
      onChange(label, {
        ...props,
        x: ((props?.x || 0) - (imageLayer.x || 0)) / imageLayer.scale - x,
        y: ((props?.y || 0) - (imageLayer?.y || 0)) / imageLayer.scale - y,
      });
    },
    [onChange, imageLayer, x, y]
  );

  const onImageDrag = useCallback(
    (event: KonvaEventObject<MouseEvent>) => {
      setImageLayer({
        ...imageLayer,
        x: event.currentTarget.attrs.x,
        y: event.currentTarget.attrs.y,
      });
    },
    [imageLayer, setImageLayer]
  );

  const boundBoxFunc = useCallback(
    (oldBoundBox: Box, newBoundBox: Box) => {
      const leftLimit = imageLayer.x + x * imageLayer.scale;
      const topLimit = imageLayer.y + y * imageLayer.scale;
      const rightLimit = leftLimit + imageWidth * imageLayer.scale;
      const bottomLimit = topLimit + imageHeight * imageLayer.scale;
      const width =
        oldBoundBox.x === newBoundBox.x
          ? // Check right limit
            newBoundBox.x + newBoundBox.width < rightLimit
            ? newBoundBox.width
            : rightLimit - oldBoundBox.x
          : // Check left limit
          leftLimit > newBoundBox.x
          ? oldBoundBox.width
          : newBoundBox.width;
      const height =
        oldBoundBox.y === newBoundBox.y
          ? // Check bottom limit
            newBoundBox.y + newBoundBox.height < bottomLimit
            ? newBoundBox.height
            : bottomLimit - oldBoundBox.y
          : // Check top limit
          topLimit > newBoundBox.y
          ? oldBoundBox.height
          : newBoundBox.height;

      return {
        width,
        height,
        rotation: newBoundBox.rotation,
        y: Math.max(topLimit, newBoundBox.y),
        x: Math.max(leftLimit, newBoundBox.x),
      } as Box;
    },
    [imageLayer, imageWidth, imageHeight, x, y]
  );

  return (
    <Hotkeys
      keyName="space"
      onKeyUp={(keyName: string) => hotKey[keyName + 'Up']()}
      onKeyDown={(keyName: string) => hotKey[keyName + 'Down']()}
    >
      <div className="annotator-stage_container">
        <KonvaStage
          scaleX={1}
          scaleY={1}
          width={width}
          ref={stageRef}
          style={{cursor}}
          name={STAGE_NAME}
          className="stage"
          height={height - STAGE_HEIGHT_ADJ}
          onWheel={onWheel}
          onMouseUp={onMouseup}
          onMouseDown={onStageMouseDown}
          onMouseOver={onStageMouseOver}
          onContextMenu={onStageContextMenu}
        >
          <Layer
            x={imageLayer.x}
            y={imageLayer.y}
            ref={imageLayerRef}
            draggable={isGrabbing}
            scaleX={imageLayer.scale}
            scaleY={imageLayer.scale}
            onDragMove={onImageDrag}
          >
            <Image
              src={src}
              x={((imageWidth - width) / 2) * getScaleDir(width, imageWidth)}
              y={((imageHeight - height) / 2) * getScaleDir(height, imageHeight)}
              onError={() => setIsImageError(true)}
              onFinishLoading={() => setIsImageBusy(false)}
              onStartLoading={() => {
                setIsImageBusy(true);
                setIsImageError(false);
              }}
            />
          </Layer>
          <Layer ref={layerRef}>
            {/* Box Annotations */}
            {!isImageBusy &&
              scaledBoxLabels?.map((label: BoxLabelProps, i) => (
                <Label
                  key={i}
                  {...label}
                  onClick={e => onLabelClick(e, label)}
                  onContextMenu={e => onLabelContextMenu(e, label)}
                  selected={selectedLabels.includes(label.id)}
                  isTransforming={isTransforming && selectedLabels.includes(label.id)}
                  onDrag={props => onLabelDrag(label, props)}
                  onTooltip={onTooltip}
                  onDelete={() => onDeleteAnnotation(label.id, 'RECT')}
                  limit={{
                    x: imageLayer.x + x * imageLayer.scale,
                    y: imageLayer.y + y * imageLayer.scale,
                    width: imageWidth * imageLayer.scale,
                    height: imageHeight * imageLayer.scale,
                  }}
                />
              ))}
            {/* Polygon */}
            {!isImageBusy &&
              localPolyLabel?.map((poly: PolyLabelProps, index) => (
                <Polygon
                  key={`polygon-${index}`}
                  data={poly}
                  width={width}
                  height={height}
                  complete={poly.complete}
                  mousePosition={mousePosition}
                  isActiveTool={tool === 'POLY'}
                  selected={selectedLabels.includes(poly.id)}
                  onComplete={onPolyLabelCreated}
                  onClick={e => onLabelClick(e, poly)}
                  onContextMenu={e => onLabelContextMenu(e, poly)}
                  onDelete={() => onDeleteAnnotation(poly.id, 'POLY')}
                />
              ))}
            {/* Lines */}
            {!isImageBusy &&
              localLineLabels?.map((line: LineLabelProps, index) => (
                <Line
                  key={`line-${index}`}
                  data={line}
                  width={width}
                  height={height}
                  complete={line.complete}
                  mousePosition={mousePosition}
                  isActiveTool={tool === 'LINE'}
                  selected={selectedLabels.includes(line.id)}
                  onComplete={onLineLabelCreated}
                  onClick={event => onLabelClick(event, line)}
                  onDelete={() => onDeleteAnnotation(line.id, 'LINE')}
                  onContextMenu={event => onLabelContextMenu(event, line)}
                />
              ))}

            {/* Box Resizer */}
            <Transformer
              ref={transformer}
              {...TRANSFORMER}
              onTransformStart={onTransformStart}
              onTransformEnd={onTransformEnd}
              onMouseOver={() => setCursor('default')}
              boundBoxFunc={boundBoxFunc}
            />
            {/* Selection rectangle */}
            <Rect {...SELECTION_BOX} {...selector} />
          </Layer>
        </KonvaStage>
        <Tooltip title={tooltip.title} open={!!tooltip.title}>
          <div
            className="annotator-tooltip_placeholder"
            style={{top: tooltip.y, left: tooltip.x}}
          />
        </Tooltip>
        {isImageBusy && (
          <div
            className="annotator-progress_placeholder"
            style={{top: height / 2 - LOADER_SIZE / 2, left: width / 2 - LOADER_SIZE / 2}}
          >
            <CircularProgress size={LOADER_SIZE} />
          </div>
        )}
        {isImageError && (
          <div className="annotator-image_broken">
            <BrokenImage style={{width: 48, height: 48, color: grey[300]}} />
          </div>
        )}
      </div>
    </Hotkeys>
  );
}

export default Annotator;
